// src/Components/Modules/PrintReceipt.js
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Button, SelectPicker } from "rsuite";
import moment from "moment";
import { AxiosAuthInstance } from "../../AxiosInterceptors";

const TEAL = "#00838a";
const GREEN = "#8dc63f";
const LIGHT = "#f0f9fa";

const resolveAsset = (path) => {
  if (!path) return "";
  if (/^(https?:\/\/|data:|blob:)/i.test(path)) return path;
  const base = AxiosAuthInstance.defaults?.baseURL || "";
  const origin = base.replace(/\/(api|api\/v\d+|v\d+)(\/.*)?$/i, "").replace(/\/$/, "");
  return origin ? `${origin}/${String(path).replace(/^\//, "")}` : path;
};

export const PrintReceipt = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const data = location.state?.billing;
  const deployment = location.state?.deployment; 
  const passedCompany = location.state?.company;

  const [receiptSize, setReceiptSize] = useState(
    () => localStorage.getItem("preferredReceiptSize") || "A4"
  );
  const [company, setCompany] = useState({});

  useEffect(() => {
    const printRoot = document.getElementById("print-receipt-root");
    const siblings = Array.from(document.body.children).filter((el) => el !== printRoot);
    siblings.forEach((el) => {
      el.dataset.prevDisplay = el.style.display;
      el.style.display = "none";
    });
    document.body.style.background = "#f0f2f5";
    return () => {
      siblings.forEach((el) => {
        el.style.display = el.dataset.prevDisplay || "";
      });
      document.body.style.background = "";
    };
  }, []);

  useEffect(() => {
    if (!data) navigate(-1);
  }, [data, navigate]);

  useEffect(() => {
    localStorage.setItem("preferredReceiptSize", receiptSize);
  }, [receiptSize]);

  useEffect(() => {
    let mounted = true;
    const normalize = (c = {}) => ({
      company_name: c.company_name || c.name || "",
      company_address: c.company_address || c.address || "",
      company_phone: c.company_phone || c.phone || "",
      company_email: c.company_email || c.email || "",
      website: c.website || c.website_url || c.site || "",
      vat_number: c.vat_number || c.mushak_number || c.vat_no || c.bin_number || c.bin || "",
      cr_number: c.cr_number || c.trade_license_no || c.cr_no || "",
      logo: c.logo || c.company_logo || c.logo_url || "",
      vat_rate: c.vat_rate ?? c.vat ?? c.vat_percentage ?? 0,
      bank_account: c.bank_account || c.account_number || c.bank_details?.account_number || "",
      bank_iban: c.bank_iban || c.iban || c.bank_details?.iban || "",
      bank_swift: c.bank_swift || c.swift_code || c.bank_details?.swift_code || "",
      bank_name: c.bank_name || c.bank || c.bank_details?.bank_name || "",
      currency: c.currency || "BDT",
    });

    (async () => {
      const passed = normalize(passedCompany);
      try {
        const res = await AxiosAuthInstance.get("company/profile");
        const api = normalize(res.data?.company || {});
        if (!mounted) return;
        const merged = { ...api };
        Object.entries(passed).forEach(([k, v]) => {
          if (v !== "" && v != null) merged[k] = v;
        });
        setCompany(merged);
      } catch {
        if (mounted) setCompany(passed);
      }
    })();
    return () => { mounted = false; };
  }, [passedCompany]);

  const handlePrint = () => {
    const onAfter = () => {
      window.removeEventListener("afterprint", onAfter);
      navigate(-1);
    };
    window.addEventListener("afterprint", onAfter);
    window.print();
  };

  const formatCurrency = (value) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: company?.currency || "BDT",
      minimumFractionDigits: 2,
    }).format(value || 0);

  const itemsPerPage = receiptSize === "Letter" ? 10 : receiptSize === "A4" ? 12 : 9999;

  if (!data) return null;

  const sheetStyle =
    receiptSize === "Thermal"
      ? { width: "80mm", minHeight: "auto", padding: "5mm" }
      : receiptSize === "Letter"
      ? { width: "216mm", minHeight: "279mm", padding: "8mm" }
      : { width: "210mm", minHeight: "297mm", padding: "8mm" };

  const pages = [];
  
  const rawItems = (deployment?.assignments && deployment.assignments.length > 0)
    ? deployment.assignments.map(a => ({
        id: a.id,
        item_name: `${a.employee?.employee_name || 'N/A'} ${a.employee?.designation ? `- ${a.employee.designation}` : ''}`,
        service_date: a.service_date,
        quantity: a.hours_worked,
        product_unit: "Hours",
        unit_price: a.hourly_rate,
        sub_total: a.total_amount
      }))
    : (data.billing_items || data.billingItems || []);

  for (let i = 0; i < rawItems.length; i += itemsPerPage) {
    pages.push(rawItems.slice(i, i + itemsPerPage));
  }
  if (pages.length === 0) pages.push([]);

  return createPortal(
    <div id="print-receipt-root">
      <style>{`
        #print-receipt-root, #print-receipt-root * { box-sizing: border-box; }
        #print-receipt-root .pr-wrap { min-height: 100vh; background: #f0f2f5; padding: 24px 12px; font-family: Arial, Helvetica, sans-serif; }
        #print-receipt-root .pr-controls { max-width: 1000px; margin: 0 auto 20px auto; background: #fff; border-radius: 8px; padding: 15px; box-shadow: 0 2px 6px rgba(0,0,0,.08); }
        #print-receipt-root .pr-sheet { background: #fff; margin: 0 auto 24px auto; box-shadow: 0 2px 14px rgba(0,0,0,.18); display: flex; flex-direction: column; }

        @media print {
          @page {
            size: ${receiptSize === "Thermal" ? "80mm auto" : receiptSize === "Letter" ? "letter portrait" : "A4 portrait"};
            margin: ${receiptSize === "Thermal" ? "0" : "6mm"};
          }
          html, body { margin: 0 !important; padding: 0 !important; background: #fff !important; }
          body > *:not(#print-receipt-root) { display: none !important; }
          #print-receipt-root .no-print { display: none !important; }
          #print-receipt-root .pr-wrap { padding: 0 !important; background: #fff !important; min-height: 0 !important; }
          #print-receipt-root .pr-sheet { width: 100% !important; padding: 0 !important; margin: 0 !important; box-shadow: none !important; page-break-after: always; }
          #print-receipt-root .pr-sheet:last-of-type { page-break-after: auto; }
          #print-receipt-root tr { page-break-inside: avoid; }
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        }

        .inv-root { font-size: 10px; color: #222; flex: 1; display: flex; flex-direction: column; }
        .inv-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
        .inv-top .logo-side img { max-height: 60px; max-width: 180px; object-fit: contain; }
        .inv-top .logo-fallback { color: ${TEAL}; font-size: 24px; font-weight: 900; font-style: italic; }
        .inv-top .company-side { text-align: right; }
        .inv-top .company-name { color: ${TEAL}; font-weight: 800; font-size: 15px; }
        .inv-top .company-ids { color: ${TEAL}; font-weight: 700; font-size: 10px; margin-top: 4px; }
        .inv-bar { background: ${TEAL}; border-bottom: 3px solid ${GREEN}; color: #fff; font-weight: 800; letter-spacing: 3px; padding: 6px 10px; font-size: 12px; }
        .info-wrap { display: flex; border: 1px solid #ccc; border-top: none; margin-bottom: 10px; }
        .info-wrap .qr-cell { width: 24%; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 6px; border-right: 1px solid #ccc; }
        .info-wrap .qr-cell small { color: #666; font-size: 8px; margin-top: 3px; }
        .inv-info-table { width: 76%; border-collapse: collapse; font-size: 9.5px; }
        .inv-info-table td { border: 1px solid #ccc; padding: 4px 8px; }
        .inv-info-table td.label { color: ${TEAL}; font-weight: 700; background: ${LIGHT}; width: 38%; }
        .party-box { border: 1px solid #ccc; font-size: 9.5px; height: 100%; }
        .party-box .party-title { background: ${TEAL}; color: #fff; font-weight: 700; padding: 4px 8px; font-size: 10px; }
        .party-box .body { padding: 6px 8px; }
        .party-box .line { margin: 2px 0; line-height: 1.4; }
        .party-box .line strong { color: ${TEAL}; display: inline-block; min-width: 62px; }
        .items-table { width: 100%; border-collapse: collapse; margin: 10px 0; font-size: 9px; }
        .items-table th { background: ${TEAL}; color: #fff; border: 1px solid ${TEAL}; border-bottom: 2px solid ${GREEN}; padding: 6px 4px; font-size: 8.5px; text-align: center; }
        .items-table td { border: 1px solid #ccc; padding: 5px 4px; text-align: center; }
        .items-table td.left { text-align: left; }
        .items-table td.right { text-align: right; font-weight: 600; }
        .payment-box { border: 1px solid #ccc; font-size: 9.5px; }
        .payment-box .pay-title { background: ${TEAL}; color: #fff; font-weight: 700; padding: 4px 8px; }
        .payment-box .body { padding: 6px 8px; }
        .payment-box .line { margin: 2px 0; }
        .payment-box .line strong { color: ${TEAL}; display: inline-block; min-width: 70px; }
        .totals-table { width: 100%; border-collapse: collapse; font-size: 9.5px; }
        .totals-table td { border: 1px solid #ccc; padding: 5px 8px; }
        .totals-table td.label { background: ${LIGHT}; color: ${TEAL}; font-weight: 700; }
        .totals-table td.amount { text-align: right; font-weight: 600; }
        .totals-table tr.grand td { background: #e6f4f5; font-weight: 800; }
        .words-box { border: 1px solid #ccc; padding: 6px 8px; font-size: 9.5px; font-style: italic; background: #fafafa; margin: 8px 0; }
        .notes-box { border: 1px solid #ccc; font-size: 9.5px; }
        .notes-box .ntitle { background: ${TEAL}; color: #fff; font-weight: 700; padding: 4px 8px; }
        .notes-box .body { padding: 6px 8px; min-height: 34px; }
        .signature-area { margin-top: 26px; text-align: right; font-size: 9.5px; }
        .signature-area .sig-line { display: inline-block; border-top: 1px solid #333; width: 190px; padding-top: 4px; font-weight: 700; }
        .inv-footer { margin-top: auto; background: ${TEAL}; border-top: 3px solid ${GREEN}; color: #fff; padding: 6px 10px; display: flex; justify-content: space-between; gap: 10px; font-size: 8px; }
        .inv-footer .f-col div { margin: 1px 0; }
        .page-footer { padding-top: 4px; text-align: right; font-size: 8.5px; color: #666; }
      `}</style>

      <div className="pr-wrap">
        <div className="pr-controls no-print">
          <div className="row align-items-center g-2">
            <div className="col-md-4">
              <label className="form-label fw-bold mb-1">Receipt Size:</label>
              <SelectPicker
                value={receiptSize}
                onChange={(v) => setReceiptSize(v)}
                data={[
                  { label: "📄 A4 (Standard Invoice)", value: "A4" },
                  { label: "🧾 Thermal (80mm Receipt)", value: "Thermal" },
                  { label: "📄 Letter (US Standard)", value: "Letter" },
                ]}
                style={{ width: "100%" }}
              />
            </div>
            <div className="col-md-4 text-md-center">
              <div className="text-muted small mt-3 mt-md-4">Pages: {pages.length}</div>
            </div>
            <div className="col-md-4 text-md-end">
              <Button appearance="primary" color="green" onClick={handlePrint} className="me-2">🖨️ Print Invoice</Button>
              <Button appearance="subtle" onClick={() => navigate(-1)}>← Back</Button>
            </div>
          </div>
        </div>

        {pages.map((pageItems, idx) => (
          <div key={idx} className={`pr-sheet size-${receiptSize}`} style={sheetStyle}>
            {receiptSize === "Thermal" ? (
              <ThermalTemplate data={data} company={company} formatCurrency={formatCurrency} />
            ) : (
              <InvoicePage
                data={data}
                company={company}
                formatCurrency={formatCurrency}
                pageItems={pageItems}
                pageNumber={idx + 1}
                totalPages={pages.length}
                isFirstPage={idx === 0}
                isLastPage={idx === pages.length - 1}
                itemsPerPage={itemsPerPage}
                isDeployment={!!deployment?.assignments}
              />
            )}
          </div>
        ))}
      </div>
    </div>,
    document.body
  );
};

const CompanyLogo = ({ company }) => {
  const [failed, setFailed] = useState(false);
  const url = resolveAsset(company?.logo);
  if (!url || failed) {
    return (
      <div className="logo-fallback">
        {(company?.company_name || "LOGO").split(" ").map((w) => w[0]).join("").slice(0, 4).toUpperCase()}
      </div>
    );
  }
  return <img src={url} alt="logo" onError={() => setFailed(true)} />;
};

const InvoicePage = ({
  data, company, formatCurrency, pageItems,
  pageNumber, totalPages, isFirstPage, isLastPage, itemsPerPage, isDeployment
}) => {
  const billDate = moment(data.billing_date || data.created_at || new Date());
  const dueDate = data.due_date ? moment(data.due_date) : billDate.clone().add(30, "days");

  const totalExcl = Number(data.total_price || 0);
  const discount = Number(data.total_discount || 0);
  const points = Number(data.point_used || 0);
  const netTotal = Number(data.net_total || 0);
  const companyRate = Number(company?.vat_rate || 0);

  let totalVat = Number(data.vat || 0);
  if (!totalVat && netTotal > totalExcl) totalVat = netTotal - totalExcl;
  if (!totalVat && companyRate > 0) totalVat = (totalExcl * companyRate) / 100;
  totalVat = Math.round(totalVat * 100) / 100;

  const effectiveRate = totalExcl > 0 ? (totalVat / totalExcl) * 100 : companyRate;
  const grandTotal = Math.max(0, totalExcl + totalVat - discount - points);

  const qrPayload = JSON.stringify({
    seller: company?.company_name,
    vat_no: company?.vat_number,
    invoice_no: data.order_number,
    date: billDate.format("DD/MM/YYYY"),
    total: grandTotal,
    vat: totalVat,
  });

  return (
    <div className="inv-root">
      {isFirstPage && (
        <>
          <div className="inv-top">
            <div className="logo-side"><CompanyLogo company={company} /></div>
            <div className="company-side">
              <div className="company-name">{company?.company_name || "—"}</div>
              <div className="company-ids">
                Vat No: {company?.vat_number || "—"} &nbsp;&nbsp; C.R No: {company?.cr_number || "—"}
              </div>
              <div style={{ fontSize: 9, color: "#555", marginTop: 2 }}>
                {company?.company_phone} {company?.company_email && `| ${company.company_email}`}
              </div>
            </div>
          </div>

          <div className="inv-bar">TAX INVOICE</div>
          <div className="info-wrap">
            <div className="qr-cell">
              <QRCodeSVG value={qrPayload} size={92} level="M" />
              <small>Scan for verification</small>
            </div>
            <table className="inv-info-table">
              <tbody>
                <tr><td className="label">Invoice No</td><td>{data.order_number}</td></tr>
                <tr><td className="label">Invoice Date</td><td>{billDate.format("DD/MM/YYYY")}</td></tr>
                <tr><td className="label">Invoice Time</td><td>{billDate.format("hh:mm A")}</td></tr>
                <tr><td className="label">Due Date</td><td>{dueDate.format("DD/MM/YYYY")}</td></tr>
                <tr><td className="label">PO Number</td><td>{data.po_number || "—"}</td></tr>
                <tr><td className="label">Project Code</td><td>{data.project_code || "—"}</td></tr>
                <tr><td className="label">Invoice Month</td><td>{billDate.format("MMM-YYYY")}</td></tr>
                <tr><td className="label">Salesperson</td><td>{data.created_by || "—"}</td></tr>
              </tbody>
            </table>
          </div>

          <div className="row g-2">
            <div className="col-6">
              <div className="party-box">
                <div className="party-title">Seller</div>
                <div className="body">
                  <div className="line"><strong>Company:</strong> {company?.company_name || "—"}</div>
                  <div className="line"><strong>Address:</strong> {company?.company_address || "—"}</div>
                  <div className="line"><strong>VAT No:</strong> {company?.vat_number || "—"}</div>
                  <div className="line"><strong>Phone:</strong> {company?.company_phone || "—"}</div>
                </div>
              </div>
            </div>
            <div className="col-6">
              <div className="party-box">
                <div className="party-title">Buyer</div>
                <div className="body">
                  <div className="line"><strong>Name:</strong> {data.customer?.name || data.customer_name || "Walk-in Customer"}</div>
                  <div className="line"><strong>Phone:</strong> {data.customer?.phone || data.customer_phone || "—"}</div>
                  <div className="line"><strong>Address:</strong> {data.customer?.address || "—"}</div>
                  <div className="line"><strong>VAT No:</strong> {data.customer?.vat_number || "—"}</div>
                  <div className="line"><strong>CR No:</strong> {data.customer?.cr_number || "—"}</div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <table className="items-table">
        <thead>
          <tr>
            <th style={{ width: "3%" }}>#</th>
            <th style={{ width: isDeployment ? "35%" : "40%" }}>Item & Description</th>
            {isDeployment && <th style={{ width: "10%" }}>Date</th>}
            <th style={{ width: "8%" }}>Qty</th>
            <th style={{ width: "7%" }}>Unit</th>
            <th style={{ width: "10%" }}>Rate</th>
            <th style={{ width: "8%" }}>VAT(%)</th>
            <th style={{ width: "11%" }}>VAT Amount</th>
            <th style={{ width: "11.5%" }}>Total Excl. VAT</th>
            <th style={{ width: "11.5%" }}>Total Incl. VAT</th>
          </tr>
        </thead>
        <tbody>
          {pageItems.map((item, i) => {
            const qty = Number(item.quantity || 0);
            const rate = Number(item.unit_price || 0);
            const excl = qty * rate;
            const vatAmt = (excl * effectiveRate) / 100;
            return (
              <tr key={item.id || i}>
                <td>{(pageNumber - 1) * itemsPerPage + i + 1}</td>
                <td className="left">
                  {item.item_name || item.item?.item_name || item.product?.name || "Service"}
                </td>
                {isDeployment && (
                  <td>{item.service_date ? moment(item.service_date).format("DD/MM/YYYY") : "—"}</td>
                )}
                <td>{qty.toFixed(2)}</td>
                <td>{item.product_unit || "—"}</td>
                <td>{formatCurrency(rate)}</td>
                <td>{effectiveRate.toFixed(2)}%</td>
                <td className="right">{formatCurrency(vatAmt)}</td>
                <td className="right">{formatCurrency(excl)}</td>
                <td className="right">{formatCurrency(excl + vatAmt)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {isLastPage && (
        <>
          <div className="row g-2">
            <div className="col-6">
              <div className="payment-box">
                <div className="pay-title">Payment Details</div>
                <div className="body">
                  <div className="line"><strong>Company:</strong> {company?.company_name || "—"}</div>
                  <div className="line"><strong>Account:</strong> {company?.bank_account || "—"}</div>
                  <div className="line"><strong>IBAN:</strong> {company?.bank_iban || "—"}</div>
                  <div className="line"><strong>Swift Code:</strong> {company?.bank_swift || "—"}</div>
                  <div className="line"><strong>Bank:</strong> {company?.bank_name || "—"}</div>
                </div>
              </div>
            </div>
            <div className="col-6">
              <table className="totals-table">
                <tbody>
                  <tr>
                    <td className="label" style={{ width: "55%" }}>Total (Excl. VAT)</td>
                    <td className="amount">{formatCurrency(totalExcl)}</td>
                  </tr>
                  <tr>
                    <td className="label">Total VAT ({effectiveRate.toFixed(2)}%)</td>
                    <td className="amount">{formatCurrency(totalVat)}</td>
                  </tr>
                  {discount > 0 && (
                    <tr><td className="label">Discount</td><td className="amount">-{formatCurrency(discount)}</td></tr>
                  )}
                  {points > 0 && (
                    <tr><td className="label">Points Used</td><td className="amount">-{formatCurrency(points)}</td></tr>
                  )}
                  <tr className="grand">
                    <td className="label">Grand Total</td>
                    <td className="amount">{formatCurrency(grandTotal)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="words-box">
            <strong>Amount in Words:</strong> {numberToWords(Math.floor(grandTotal))} Only
          </div>

          <table className="totals-table">
            <tbody>
              <tr className="grand">
                <td className="label" style={{ width: "70%" }}>Balance Due</td>
                <td className="amount">{formatCurrency(grandTotal)}</td>
              </tr>
            </tbody>
          </table>

          <div className="row g-2 mt-2">
            <div className="col-7">
              <div className="notes-box">
                <div className="ntitle">Notes</div>
                <div className="body">{data.notes || "Looking forward for your business"}</div>
              </div>
            </div>
            <div className="col-5">
              <div className="signature-area">
                <span className="sig-line">Authorized Signature</span>
              </div>
            </div>
          </div>
        </>
      )}

      <div className="inv-footer">
        <div className="f-col">
          <div>🌐 {company?.website || "—"}</div>
          <div>✉️ {company?.company_email || "—"}</div>
        </div>
        <div className="f-col">
          <div>📞 {company?.company_phone || "—"}</div>
        </div>
        <div className="f-col" style={{ maxWidth: "45%", textAlign: "right" }}>
          <div>📍 {company?.company_address || "—"}</div>
        </div>
      </div>

      <div className="page-footer">Page {pageNumber} of {totalPages}</div>
    </div>
  );
};

const numberToWords = (num) => {
  if (num === 0) return "Zero";
  const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const convert = (n) => {
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : "");
    if (n < 1000) return ones[Math.floor(n / 100)] + " Hundred" + (n % 100 ? " " + convert(n % 100) : "");
    if (n < 1000000) return convert(Math.floor(n / 1000)) + " Thousand" + (n % 1000 ? " " + convert(n % 1000) : "");
    if (n < 1000000000) return convert(Math.floor(n / 1000000)) + " Million" + (n % 1000000 ? " " + convert(n % 1000000) : "");
    return String(n);
  };
  return convert(num);
};

const ThermalTemplate = ({ data, company, formatCurrency }) => (
  <div style={{ fontFamily: "monospace", fontSize: "10px" }}>
    <div style={{ textAlign: "center", marginBottom: 8 }}>
      <CompanyLogo company={company} />
      <h3 style={{ margin: "4px 0", fontSize: 14 }}>{company?.company_name}</h3>
      <p style={{ margin: "2px 0", fontSize: 9 }}>{company?.company_address}</p>
      <p style={{ margin: "2px 0", fontSize: 9 }}>Tel: {company?.company_phone}</p>
      <p style={{ margin: "2px 0", fontSize: 9 }}>VAT/Mushak: {company?.vat_number}</p>
    </div>
    <div style={{ borderTop: "1px dashed #000", borderBottom: "1px dashed #000", padding: "4px 0", margin: "8px 0", textAlign: "center", fontWeight: "bold" }}>
      TAX INVOICE
    </div>
    <p style={{ margin: "2px 0" }}>Invoice: #{data.order_number}</p>
    <p style={{ margin: "2px 0" }}>Date: {moment(data.billing_date || data.created_at).format("DD/MM/YYYY hh:mm A")}</p>
    <table style={{ width: "100%", fontSize: 9, margin: "8px 0", borderCollapse: "collapse" }}>
      <thead>
        <tr style={{ borderBottom: "1px dashed #000" }}>
          <th style={{ textAlign: "left", padding: 3 }}>Item</th>
          <th style={{ textAlign: "right", padding: 3 }}>Qty</th>
          <th style={{ textAlign: "right", padding: 3 }}>Total</th>
        </tr>
      </thead>
      <tbody>
        {(data.billing_items || data.billingItems || []).map((item, i) => (
          <tr key={i}>
            <td style={{ padding: 3 }}>{item.item?.item_name || item.item_name}</td>
            <td style={{ padding: 3, textAlign: "right" }}>{item.quantity}</td>
            <td style={{ padding: 3, textAlign: "right" }}>{formatCurrency(item.sub_total)}</td>
          </tr>
        ))}
      </tbody>
    </table>
    <div style={{ borderTop: "1px dashed #000", paddingTop: 4 }}>
      <div style={{ display: "flex", justifyContent: "space-between" }}><span>Total:</span><span>{formatCurrency(data.total_price)}</span></div>
      <div style={{ display: "flex", justifyContent: "space-between" }}><span>VAT:</span><span>{formatCurrency(data.vat || 0)}</span></div>
      <div style={{ display: "flex", justifyContent: "space-between", fontWeight: "bold", fontSize: 11, marginTop: 4 }}>
        <span>GRAND TOTAL:</span><span>{formatCurrency(data.total_amount_to_be_paid)}</span>
      </div>
    </div>
    <div style={{ textAlign: "center", marginTop: 10 }}>
      <p style={{ margin: "6px 0", fontWeight: "bold" }}>Thank you for your business!</p>
    </div>
  </div>
);

export default PrintReceipt;